import { BTLocalStorage } from "types/btStorage";

type FilterScalar = string | number | boolean;

/**
 * A filter as the agent writes it: a value to equal, or an op such as
 * `{ op: "window", value: "next week" }` or `{ op: "in", value: [0, 1] }`.
 */
export type AgentFilterValue =
    | FilterScalar
    | { op: string; value: FilterScalar | FilterScalar[] | Record<string, FilterScalar> };

/** The query behind a suggested question's count, as the service runs it. */
export interface IAskQuery {
    entity_type: string;
    filters: Record<string, AgentFilterValue>;
    sort?: string;
    /** An aggregate when present ("count"); a listing otherwise. */
    metric?: string;
    field?: string;
    group_by?: string[];
}

/**
 * The record a suggestion is about: a job, lead, contact or sub the words
 * being typed could name.
 */
export interface IAskSuggestRecord {
    entity: string;
    id: string;
    title: string;
    /** Where in the typed text the name begins; picking it replaces from here. */
    start: number;
}

/** Where a suggested question came from. */
export type AskSuggestSource = "sentence" | "record" | "kind" | "attention" | "opening" | "recent";

/** How a count reads: a count of overdue things is a warning, not just a number. */
export type AskSuggestTone = "negative" | "warning" | "neutral";

/** One question offered while typing. */
export interface IAskSuggestQuestion {
    text: string;
    source: AskSuggestSource;
    /** The query behind its count, when a count means something. */
    query: IAskQuery | null;
    tone: AskSuggestTone;
    /** An opening to finish ("What's overdue on"): picking it fills the box. */
    fill: boolean;
}

/** The gray text after the caret: the box's whole text once Tab takes it. */
export interface IAskCompletion {
    text: string;
    kind: "name" | "opening";
}

/** A prediction of the query being typed: the typed text, finished. */
export interface IAskPredictedQuery {
    text: string;
    /** "name": finished from the account's own names; "vocab": a kind, a state or a window. */
    source: "name" | "vocab";
    /** For a name word: how many records carry it. */
    count?: number;
}

export interface IAskSuggestions {
    completion: IAskCompletion | null;
    /** What the user is most likely typing, best first. */
    queries: IAskPredictedQuery[];
    records: IAskSuggestRecord[];
    questions: IAskSuggestQuestion[];
    /** The record the questions are about, when there is one. */
    about: { entity: string; id: string; title: string } | null;
}

export const NO_SUGGESTIONS: IAskSuggestions = {
    completion: null,
    queries: [],
    records: [],
    questions: [],
    about: null,
};

/** The subject the conversation is on, sent so an empty box suggests follow-ups. */
export interface IAskSuggestSubject {
    entity: string;
    id: string;
    title: string;
}

interface IServiceInput {
    searchBaseUrl: string;
    searchApiVersion: string;
    getToken: () => Promise<string>;
    clearToken: () => void;
    signal?: AbortSignal;
}

async function authorized(
    input: IServiceInput,
    request: (token: string) => Promise<Response>,
): Promise<Response | null> {
    const send = async (): Promise<Response | null> => {
        let token: string;
        try {
            token = await input.getToken();
        } catch {
            return null;
        }
        return request(token);
    };
    let response = await send();
    if (response !== null && response.status === 401) {
        input.clearToken();
        response = await send();
    }
    return response !== null && response.ok ? response : null;
}

function isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === "object" && value !== null && !Array.isArray(value);
}

const SOURCES = new Set<AskSuggestSource>([
    "sentence",
    "record",
    "kind",
    "attention",
    "opening",
    "recent",
]);
const TONES = new Set<AskSuggestTone>(["negative", "warning", "neutral"]);

/** The suggestions the service sent, keeping only what this page can draw. */
export function readSuggestions(raw: unknown): IAskSuggestions {
    if (!isRecord(raw)) {
        return NO_SUGGESTIONS;
    }
    const completionKind = isRecord(raw.completion) ? raw.completion.kind : undefined;
    const completion: IAskCompletion | null =
        isRecord(raw.completion) &&
        typeof raw.completion.text === "string" &&
        (completionKind === "name" || completionKind === "opening")
            ? { text: raw.completion.text, kind: completionKind }
            : null;
    const records = Array.isArray(raw.records)
        ? raw.records.flatMap((r): IAskSuggestRecord[] =>
              isRecord(r) &&
              typeof r.entity === "string" &&
              typeof r.id === "string" &&
              typeof r.title === "string" &&
              typeof r.start === "number"
                  ? [{ entity: r.entity, id: r.id, title: r.title, start: r.start }]
                  : [],
          )
        : [];
    const questions = Array.isArray(raw.questions)
        ? raw.questions.flatMap((q): IAskSuggestQuestion[] =>
              isRecord(q) && typeof q.text === "string" && q.text.trim().length > 0
                  ? [
                        {
                            text: q.text,
                            source: SOURCES.has(q.source as AskSuggestSource)
                                ? (q.source as AskSuggestSource)
                                : "kind",
                            query:
                                isRecord(q.query) && typeof q.query.entity_type === "string"
                                    ? (q.query as unknown as IAskQuery)
                                    : null,
                            tone: TONES.has(q.tone as AskSuggestTone)
                                ? (q.tone as AskSuggestTone)
                                : "neutral",
                            fill: q.fill === true,
                        },
                    ]
                  : [],
          )
        : [];
    const about =
        isRecord(raw.about) &&
        typeof raw.about.entity === "string" &&
        typeof raw.about.id === "string" &&
        typeof raw.about.title === "string"
            ? { entity: raw.about.entity, id: raw.about.id, title: raw.about.title }
            : null;
    const queries = Array.isArray(raw.queries)
        ? raw.queries.flatMap((q): IAskPredictedQuery[] =>
              isRecord(q) && typeof q.text === "string" && q.text.trim().length > 0
                  ? [
                        {
                            text: q.text,
                            source: q.source === "name" ? "name" : "vocab",
                            ...(typeof q.count === "number" ? { count: q.count } : {}),
                        },
                    ]
                  : [],
          )
        : [];
    return { completion, queries, records, questions, about };
}

/**
 * What to offer for the text in the box. Null when the service could not say
 * (a 404 means it has no suggestions route; the box works without one).
 */
export async function fetchAskSuggestions(
    input: IServiceInput & { text: string; subject: IAskSuggestSubject | null },
): Promise<IAskSuggestions | null> {
    if (input.searchBaseUrl.length === 0) {
        return null;
    }
    const params = new URLSearchParams({ q: input.text });
    if (input.subject !== null) {
        params.set("subject_entity", input.subject.entity);
        params.set("subject_id", input.subject.id);
        params.set("subject_title", input.subject.title);
    }
    const response = await authorized(input, (token) =>
        fetch(`${input.searchBaseUrl}${input.searchApiVersion}/ask/suggest?${params.toString()}`, {
            headers: { Authorization: `Bearer ${token}` },
            signal: input.signal,
        }),
    );
    if (response === null) {
        return null;
    }
    return readSuggestions(await response.json());
}

/** The count behind each query, in order; null where one could not be read. */
export async function fetchAskSuggestCounts(
    input: IServiceInput & { queries: readonly IAskQuery[] },
): Promise<(number | null)[] | null> {
    if (input.searchBaseUrl.length === 0 || input.queries.length === 0) {
        return null;
    }
    const response = await authorized(input, (token) =>
        fetch(`${input.searchBaseUrl}${input.searchApiVersion}/ask/suggest/counts`, {
            method: "POST",
            headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
            body: JSON.stringify({ queries: input.queries }),
            signal: input.signal,
        }),
    );
    if (response === null) {
        return null;
    }
    const body = (await response.json()) as { counts?: unknown } | null;
    if (body === null || !Array.isArray(body.counts)) {
        return null;
    }
    return body.counts.map((n) => (typeof n === "number" ? n : null));
}

/** A question this user asked, offered again when its words are typed. */
export interface IAskRecentQuestion {
    text: string;
    /** Scopes the list so a shared machine doesn't offer one user's questions to another. */
    owner: string;
    /** Epoch ms, newest first. */
    askedAt: number;
}

const MAX_RECENT_QUESTIONS = 30;

/** This user's questions, newest first. Best-effort: storage can throw. */
export function getRecentQuestions(owner: string): IAskRecentQuestion[] {
    try {
        return BTLocalStorage.get("bt-objectArray-searchAskRecentQuestions")
            .filter((q) => q.owner === owner)
            .sort((a, b) => b.askedAt - a.askedAt);
    } catch {
        return [];
    }
}

/** Remembers a question asked, newest first, once per wording. */
export function addRecentQuestion(owner: string, text: string, now = Date.now()): void {
    const question = text.trim();
    if (question.length === 0) {
        return;
    }
    try {
        const stored = BTLocalStorage.get("bt-objectArray-searchAskRecentQuestions");
        const key = question.toLowerCase();
        const mine = stored.filter((q) => q.owner === owner && q.text.toLowerCase() !== key);
        const others = stored.filter((q) => q.owner !== owner);
        BTLocalStorage.set("bt-objectArray-searchAskRecentQuestions", [
            ...others,
            { text: question, owner, askedAt: now },
            ...mine.sort((a, b) => b.askedAt - a.askedAt).slice(0, MAX_RECENT_QUESTIONS - 1),
        ]);
    } catch {
        // Nothing to do: suggestions work without them.
    }
}

/**
 * The recent questions the typed text could be heading back to: every word
 * typed is the start of a word in the question. At most `limit`, and not the
 * text already in the box.
 */
export function matchRecentQuestions(
    recent: readonly IAskRecentQuestion[],
    typed: string,
    limit = 2,
): string[] {
    const words = typed.toLowerCase().match(/[\p{L}\p{N}][\p{L}\p{N}'-]*/gu) ?? [];
    if (words.length === 0) {
        return [];
    }
    const whole = typed.trim().toLowerCase();
    return recent
        .filter((q) => {
            const text = q.text.toLowerCase();
            if (text === whole) {
                return false;
            }
            const have = text.match(/[\p{L}\p{N}][\p{L}\p{N}'-]*/gu) ?? [];
            return words.every((w) => have.some((h) => h.startsWith(w)));
        })
        .slice(0, limit)
        .map((q) => q.text);
}
